import powerBubbleComputeStockData from "./powerBubbleComputeStock.json"
// Frontier ground truth imported from the model spreadsheet's
// `power-distribution` tab (see scripts/planA/applySheetUpdates/frontierLabsJson.ts).
// Regenerated by `npm run convert-split-plana`. Flip years cover the labs the
// sheet names; the per-region counts cover its anonymous starred ranks.
import frontierFlipYears from "./frontierFlipYears.json"
import frontierCountsByRegion from "./frontierCountsByRegion.json"

export type PowerRegionId = "america" | "china" | "world"
export type PowerLabId =
  | "openai"
  | "google-deepmind"
  | "anthropic"
  | "xai"
  | "meta"
  | "alibaba"
  | "bytedance"
  | "deepseek"
  | "tencent"
  | "mistral"
  | "cohere"
  | "humain"
  // Plan B's merged national megaprojects — distinct from the "Rest of X"
  // tail ids so the two never collide in one region's cell list.
  | "us-national-project"
  | "chinese-megaproject"
  | "US-tail"
  | "CH-tail"
  | "RoW-tail"

export type PowerLabData = {
  id: PowerLabId
  name: string
  regionId: PowerRegionId
  compute: number
  // When true, render as a border-only square (no fill). Marks labs the
  // spreadsheet doesn't star as frontier at this date (see
  // markFrontierByRegionCounts / isLabFrontierAtYear).
  borderOnly?: boolean
}

// The spreadsheet's `power-distribution` tab is the ONLY source of frontier
// status: a lab is frontier iff the sheet stars it. Labs the sheet names get
// a flip year in frontierFlipYears.json; a lab with no entry is never
// frontier via this lookup (anonymous starred ranks are covered by the
// per-region counts below instead).
export function isLabFrontierAtYear(labId: string, year: number): boolean {
  const flipYear = (frontierFlipYears as Record<string, number>)[labId]
  return flipYear !== undefined && year >= flipYear
}

// Per-region count of starred ranks per sheet year column — how many of a
// region's compute holders the sheet marks frontier. The sheet leaves ranks
// 6+ anonymous, but its holders are ordered by size and it stars from the
// top, so the builders satisfy the count by marking each region's largest
// holders (markFrontierByRegionCounts below).
export type FrontierRegionKey = 'US' | 'CH' | 'RoW'

const frontierCounts = frontierCountsByRegion as {
  dates: number[]
} & Record<FrontierRegionKey, number[]>

export function frontierCountForRegion(regionKey: FrontierRegionKey, d: number): number {
  const { dates } = frontierCounts
  // Step lookup: the count of the latest sheet column at or before d
  // (clamped to the first/last column outside the sheet's horizon).
  let idx = 0
  for (let i = 0; i < dates.length; i++) {
    if (dates[i] <= d) idx = i
    else break
  }
  return frontierCounts[regionKey][idx] ?? 0
}

// Mark a region's cells per the sheet's frontier count: exactly N of the
// region's individual holders are frontier (borderOnly false), everything
// else border-only. Labs the sheet names and stars (flip year ≤ d) claim
// slots first — the sheet identifies them explicitly — then the region's
// largest remaining holders fill the anonymous starred ranks. The collapsed
// "Rest of X" aggregates stand for many holders at once, none of them
// starred while collapsed, so they are never frontier and consume no slot.
const AGGREGATE_LAB_IDS = new Set<string>(['US-tail', 'CH-tail', 'RoW-tail', 'tail'])

export function markFrontierByRegionCounts(
  labs: PowerLabData[],
  regionKey: FrontierRegionKey,
  d: number,
): PowerLabData[] {
  const count = frontierCountForRegion(regionKey, d)
  const bySize = labs
    .filter(lab => !AGGREGATE_LAB_IDS.has(lab.id))
    .sort((a, b) => b.compute - a.compute)
  const namedStarred = bySize.filter(lab => isLabFrontierAtYear(lab.id, d))
  const anonymous = bySize.filter(lab => !isLabFrontierAtYear(lab.id, d))
  const frontierIds = new Set(
    [...namedStarred, ...anonymous].slice(0, count).map(lab => lab.id),
  )
  return labs.map(lab => ({ ...lab, borderOnly: !frontierIds.has(lab.id) }))
}

export type PowerRegionData = {
  id: PowerRegionId
  name: string
  shortName: string
  computeStock: number
  labs: PowerLabData[]
}

// Aggregate cell for each region's residual tail compute. Source of truth for
// the "Rest of X" label shown in the chart and the tooltip. Non-frontier labs
// are NOT folded in here — they render as individual border-only cells; this
// cell only carries the compute no individual holder accounts for (plus
// whatever the treemap's sliver prune rolls in at render time).
export const REST_OF_REGION_LABS: Record<PowerRegionId, { id: PowerLabId, name: string }> = {
  america: { id: "US-tail", name: "Rest of US" },
  china: { id: "CH-tail", name: "Rest of China" },
  world: { id: "RoW-tail", name: "Rest of World" },
}

export type PowerBubbleChartYearData = {
  year: number
  regions: PowerRegionData[]
}

export const POWER_BUBBLE_CHART_REFERENCE_YEAR = 2026

type PowerRegionStock = Record<PowerRegionId, number>

type PowerRegionDefinition = {
  id: PowerRegionId
  name: string
  shortName: string
}

type PowerLabDefinition = {
  id: PowerLabId
  name: string
  regionId: PowerRegionId
  compute2026: number
}

const powerBubbleChartYears = powerBubbleComputeStockData.map(entry => entry.year)

// Country stock values (H100e) come from the Plan A spreadsheet snapshot,
// rows "US compute stock" / "CH compute stock" / "RoW compute stock".
// Re-generate via `npx tsx scripts/convertAndSplitPlanA.ts` (or its sheet-only wiring in scripts/planA/applySheetUpdates.ts). The script
// parses unit suffixes (e.g. the row shifts from "300.0M" to "720.3M" to
// "2.4B"), so each value here is already a plain absolute number.
const countryComputeStockByYear: Record<number, PowerRegionStock> = Object.fromEntries(
  powerBubbleComputeStockData.map(entry => [
    entry.year,
    { america: entry.america, china: entry.china, world: entry.world },
  ])
)

const powerRegionDefinitions: PowerRegionDefinition[] = [
  { id: "america", name: "America", shortName: "US" },
  { id: "china", name: "China", shortName: "China" },
  { id: "world", name: "Rest of World", shortName: "RoW" },
]

// Lab values are 2026 H100e estimates from public cluster/capex reporting.
// Later years keep the same lab share of the lab's parent-country compute stock.
const powerLabDefinitions: PowerLabDefinition[] = [
  { id: "xai", name: "xAI", regionId: "america", compute2026: 1_400_000 },
  { id: "google-deepmind", name: "Google DeepMind", regionId: "america", compute2026: 1_000_000 },
  { id: "anthropic", name: "Anthropic", regionId: "america", compute2026: 500_000 },
  { id: "openai", name: "OpenAI", regionId: "america", compute2026: 400_000 },
  { id: "meta", name: "Meta AI", regionId: "america", compute2026: 300_000 },
  { id: "alibaba", name: "Alibaba Qwen", regionId: "china", compute2026: 700_000 },
  { id: "bytedance", name: "ByteDance", regionId: "china", compute2026: 480_000 },
  { id: "tencent", name: "Tencent Hunyuan", regionId: "china", compute2026: 170_000 },
  { id: "deepseek", name: "DeepSeek", regionId: "china", compute2026: 50_000 },
  { id: "humain", name: "Humain", regionId: "world", compute2026: 45_500 },
  { id: "mistral", name: "Mistral", regionId: "world", compute2026: 35_000 },
  { id: "cohere", name: "Cohere", regionId: "world", compute2026: 20_000 },
]

const getLabComputeForYear = (lab: PowerLabDefinition, year: number) => {
  const countryComputeStock = countryComputeStockByYear[year][lab.regionId]
  const referenceCountryComputeStock = countryComputeStockByYear[2026][lab.regionId]
  return Math.round(countryComputeStock * lab.compute2026 / referenceCountryComputeStock)
}

const REGION_ID_TO_FRONTIER_KEY: Record<PowerRegionId, FrontierRegionKey> = {
  america: 'US',
  china: 'CH',
  world: 'RoW',
}

const getRegionDataForYear = (
  region: PowerRegionDefinition,
  year: number
): PowerRegionData => ({
  ...region,
  computeStock: countryComputeStockByYear[year][region.id],
  labs: markFrontierByRegionCounts(
    powerLabDefinitions
      .filter(lab => lab.regionId === region.id)
      .map(lab => ({
        id: lab.id,
        name: lab.name,
        regionId: lab.regionId,
        compute: getLabComputeForYear(lab, year),
      })),
    REGION_ID_TO_FRONTIER_KEY[region.id],
    year,
  ),
})

export const powerBubbleChartData: PowerBubbleChartYearData[] = powerBubbleChartYears.map(year => ({
  year,
  regions: powerRegionDefinitions.map(region => getRegionDataForYear(region, year)),
}))

export const getPowerBubbleChartYearData = (year: number) => (
  powerBubbleChartData.find(entry => entry.year === year) ?? powerBubbleChartData[0]
)
